import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import TradeShowSearch from "./TradeShowSearch";
import ManualWorkspaceForm from "./ManualWorkspaceForm";
import api from "../../../services/api/axios";
import { normalizeTradeShowUrl } from "./TradeShowsHelpers";
import {
  buildWorkspacePayloadFromTradeShow,
  formatTradeShowLocation,
} from "./tradeShowWorkspacePayload";
import { isMappableTradeShow } from "./tradeShowMappingAvailability";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

function clean(value) {
  return String(value ?? "").trim();
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
}

function normalizeSearchRow(row) {
  return {
    id: clean(row.id),
    dynamicsId: clean(
      row.dynamicsId ||
        row.dynamics_id ||
        (row.source === "dynamics" ? row.id : ""),
    ),
    externalId: clean(row.externalId || row.external_id || row.id),
    source: clean(row.source || "database"),
    name: clean(row.name),
    startDate: clean(row.startDate || row.start_date_raw || row.start_date),
    endDate: clean(row.endDate || row.end_date_raw || row.end_date),
    dateRange: clean(row.dateRange || row.date_range),
    city: clean(row.city),
    state: clean(row.state),
    country: clean(row.country),
    industry: clean(row.industry),
    website: normalizeTradeShowUrl(row.website),
    attendees: row.attendees ?? "",
    exhibitors: row.exhibitors ?? "",
    exhibitor_directory_url: normalizeTradeShowUrl(row.exhibitor_directory_url),
    exhibitor_provider: clean(row.exhibitor_provider),
    exhibitor_directory_available: toBoolean(row.exhibitor_directory_available),
    exhibitor_directory_status: clean(row.exhibitor_directory_status),
    exhibitor_directory_checked_at: clean(row.exhibitor_directory_checked_at),
    exhibitor_directory_error: clean(row.exhibitor_directory_error),
  };
}

function normalizeSavedRow(row) {
  return {
    id: clean(row.dynamics_id || row.id),
    dynamicsId: clean(row.dynamics_id),
    source: "saved",
    name: clean(row.name),
    startDate: clean(row.start_date),
    endDate: clean(row.end_date),
    dateRange: [clean(row.start_date), clean(row.end_date)]
      .filter(Boolean)
      .join(" - "),
    city: clean(row.city),
    state: clean(row.state),
    country: clean(row.country),
    industry: clean(row.industry),
    website: normalizeTradeShowUrl(row.website),
    attendees: "",
    exhibitors: "",
    exhibitor_directory_url: normalizeTradeShowUrl(row.exhibitor_directory_url),
    exhibitor_provider: clean(row.exhibitor_provider),
    exhibitor_directory_available: toBoolean(row.exhibitor_directory_available),
    exhibitor_directory_status: clean(row.exhibitor_directory_status),
    exhibitor_directory_checked_at: clean(row.exhibitor_directory_checked_at),
    exhibitor_directory_error: clean(row.exhibitor_directory_error),
  };
}

function hasConfirmedExhibitorMapping(show) {
  return show?.exhibitor_directory_status === "mappable" && isMappableTradeShow(show);
}

function exhibitorDirectoryLabel(show) {
  if (hasConfirmedExhibitorMapping(show)) {
    return `Exhibitor mapping available: ${show.exhibitor_provider}`;
  }
  if (toBoolean(show?.exhibitor_directory_available) && clean(show?.exhibitor_directory_url)) {
    const provider = clean(show.exhibitor_provider);
    return `Exhibitor directory available${provider ? `: ${provider}` : ""} (automatic import not confirmed)`;
  }
  return "Upload an exhibitor list after creating the workspace";
}

function shouldRefreshMappability(rows) {
  return rows.some((row) =>
    ["", "unchecked", "error"].includes(clean(row.exhibitor_directory_status)),
  );
}

function dateValue(value) {
  const time = Date.parse(clean(value));
  return Number.isNaN(time) ? null : time;
}

function isPastTradeShow(show) {
  const time = dateValue(show.endDate || show.startDate);
  if (time === null) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return time < today.getTime();
}

function mappingRank(show) {
  if (hasConfirmedExhibitorMapping(show)) return 0;
  if (toBoolean(show.exhibitor_directory_available) && clean(show.exhibitor_directory_url)) return 1;
  return 2;
}

function sortTradeShowRows(a, b) {
  const rankDifference = mappingRank(a) - mappingRank(b);
  if (rankDifference) return rankDifference;

  const aPast = isPastTradeShow(a);
  const bPast = isPastTradeShow(b);
  if (aPast !== bPast) return aPast ? 1 : -1;

  const aStart = dateValue(a.startDate);
  const bStart = dateValue(b.startDate);
  if (aStart !== null && bStart !== null && aStart !== bStart) {
    return aStart - bStart;
  }
  if (aStart !== null) return -1;
  if (bStart !== null) return 1;
  return clean(a.name).localeCompare(clean(b.name));
}

export default function TradeShowWorkspaceSelector({ onCreateWorkspace }) {
  const [search, setSearch] = useState("");
  const [availableRows, setAvailableRows] = useState([]);
  const [savedRows, setSavedRows] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let canceled = false;
    const controller = new AbortController();
    async function loadSaved() {
      try {
        const response = await api.get("/user-trade-shows", {
          params: { limit: 25 },
          signal: controller.signal,
        });
        if (!canceled) {
          const normalizedRows = Array.isArray(response.data)
            ? response.data.map(normalizeSavedRow)
            : [];
          setSavedRows(normalizedRows);
          if (shouldRefreshMappability(normalizedRows)) {
            api
              .post("/user-trade-shows/mappability/refresh", null, {
                params: { limit: 25 },
                timeout: 10000,
                signal: controller.signal,
              })
              .catch(() => {});
          }
        }
      } catch {
        if (!canceled) setSavedRows([]);
      }
    }
    loadSaved();
    return () => {
      canceled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let canceled = false;
    const controller = new AbortController();
    async function loadAvailableTradeShows() {
      setAvailableLoading(true);
      setAvailableError("");
      const loadedRows = [];
      const seenCursors = new Set();
      let cursor = null;
      try {
        do {
          const response = await api.get("/trade-show-search/available", {
            params: { limit: 500, ...(cursor ? { cursor } : {}) },
            timeout: 60000,
            signal: controller.signal,
          });
          if (canceled) return;
          loadedRows.push(...(Array.isArray(response.data?.items) ? response.data.items : []).map(normalizeSearchRow));
          setAvailableRows([...loadedRows]);
          const nextCursor = clean(response.data?.next_cursor);
          if (!nextCursor) break;
          if (seenCursors.has(nextCursor)) throw new Error("Repeated trade show page cursor");
          seenCursors.add(nextCursor);
          cursor = nextCursor;
        } while (true);
      } catch {
        if (!canceled) setAvailableError("Some database trade shows could not be loaded. Refresh to try again.");
      } finally {
        if (!canceled) setAvailableLoading(false);
      }
    }
    loadAvailableTradeShows();
    return () => {
      canceled = true;
      controller.abort();
    };
  }, []);

  const rows = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const matchesSearch = (row) => {
      if (!searchTerm) return true;
      return [
        row.name,
        row.city,
        row.state,
        row.country,
        row.industry,
        row.website,
        row.exhibitor_provider,
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm);
    };
    const matchesSaved = savedRows.filter(matchesSearch);
    const matchesAvailableDatabase = availableRows.filter(matchesSearch);
    const seen = new Set();
    return [
      ...matchesSaved.filter(hasConfirmedExhibitorMapping),
      ...matchesAvailableDatabase.filter(hasConfirmedExhibitorMapping),
      ...matchesSaved.filter((row) => !hasConfirmedExhibitorMapping(row)),
      ...matchesAvailableDatabase.filter((row) => !hasConfirmedExhibitorMapping(row)),
    ]
      .filter((row) => {
        const key = row.dynamicsId || `${row.name}:${row.startDate}:${row.city}`;
        if (!row.name || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort(sortTradeShowRows);
  }, [availableRows, savedRows, search]);

  const selectedShow =
    rows.find((row) => `${row.source}:${row.id}` === selectedKey) || null;
  const canCreateWorkspace = Boolean(selectedShow);

  async function handleCreate() {
    if (!canCreateWorkspace || creating) return;
    setCreating(true);
    setError("");
    try {
      await onCreateWorkspace(
        buildWorkspacePayloadFromTradeShow(selectedShow, {
          autoImport: hasConfirmedExhibitorMapping(selectedShow),
        }),
      );
      setSearch("");
      setSelectedKey("");
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(
        (typeof detail === "object" && detail?.message) ||
          (typeof detail === "string" && detail) ||
          "Unable to create the trade show workspace.",
      );
    } finally {
      setCreating(false);
    }
  }

  return (
    <Box
      sx={{
        border: "1.5px solid #dce4ef",
        borderRadius: 2,
        backgroundColor: "#fff",
        p: 2.5,
        mb: 3,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "flex-start" }}
        spacing={2}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: BRAND_BLUE, mb: 0.5 }}>
            Create a Workspace From a Trade Show
          </Typography>
          <Typography sx={{ fontSize: "0.86rem", color: "#5a6a80", mb: 1.5 }}>
            Select a show to create a workspace. Supported directories import automatically; other shows can use an uploaded exhibitor list.
          </Typography>
          <TradeShowSearch
            value={search}
            onChange={setSearch}
            maxWidth="100%"
          />
        </Box>
        <Button
          variant="contained"
          startIcon={
            creating ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          disabled={!canCreateWorkspace || creating}
          onClick={handleCreate}
          sx={{
            alignSelf: { xs: "stretch", md: "flex-start" },
            backgroundColor: BRAND_BLUE,
            textTransform: "none",
            fontWeight: 800,
            minWidth: 180,
            mt: { xs: 0, md: 0.25 },
          }}
        >
          Create Workspace
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      {availableError && <Alert severity="warning" sx={{ mt: 1.5 }}>{availableError}</Alert>}

      <Box sx={{ mt: 2, display: "grid", gap: 1 }}>
        {availableLoading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#5a6a80",
            }}
          >
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: "0.84rem" }}>
              Loading database trade shows...
            </Typography>
          </Box>
        )}
        {!availableLoading && rows.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#7a8fa8",
            }}
          >
            <SearchIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.84rem" }}>
              {search.trim().length === 0
                ? "No saved or database trade shows are available yet."
                : "No matching saved or database trade shows found. Create one manually below."}
            </Typography>
          </Box>
        )}
        {rows.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gap: 1,
              maxHeight: rows.length > 5 ? 390 : "none",
              overflowY: rows.length > 5 ? "auto" : "visible",
              pr: rows.length > 5 ? 0.75 : 0,
            }}
          >
            {rows.map((show) => {
              const key = `${show.source}:${show.id}`;
              const selected = selectedKey === key;
              return (
                <Box
                  key={key}
                  onClick={() => setSelectedKey(key)}
                  sx={{
                    border: `1.5px solid ${selected ? BRAND_GREEN : "#edf1f6"}`,
                    borderRadius: 1.5,
                    p: 1.25,
                    cursor: "pointer",
                    backgroundColor: selected
                      ? "rgba(100,131,21,0.06)"
                      : "#fff",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                  >
                    <SmartToyIcon
                      sx={{
                        color: selected || show.source === "saved" ? BRAND_GREEN : BRAND_BLUE,
                        fontSize: 19,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: BRAND_BLUE,
                          fontSize: "0.92rem",
                        }}
                      >
                        {show.name}
                      </Typography>
                      <Typography
                        sx={{ color: "#5a6a80", fontSize: "0.78rem" }}
                      >
                        {[
                          show.dateRange,
                          formatTradeShowLocation(show),
                          show.industry,
                        ]
                          .filter(Boolean)
                          .join(" | ") || "Trade show"}
                      </Typography>
                      <Typography sx={{ color: BRAND_GREEN, fontSize: "0.74rem", fontWeight: 800, mt: 0.25 }}>
                        {exhibitorDirectoryLabel(show)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
      <ManualWorkspaceForm onCreateWorkspace={onCreateWorkspace} />
    </Box>
  );
}
